import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo, SlideIcon, Spinner, hueFor, hueInk } from "../components/Icons";
import { Confetti } from "../components/live/Confetti";
import { Podium, type Leader } from "../components/live/Podium";
import { Results } from "../components/live/Results";
import { useAuth } from "../auth/AuthProvider";
import { live } from "../lib/api";
import type { SlideType, Stats } from "../lib/types";

interface SummarySlide {
  id: number;
  position: number;
  type: SlideType;
  question: string;
  config: Record<string, any>;
  stats: Stats;
  headline: { stat: string; sub: string };
}

interface SummaryData {
  session: { id: number; code: string; total_slides: number };
  participants: number;
  total_answers: number;
  leaderboard: Leader[];
  slides: SummarySlide[];
}

export default function Summary() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const id = Number(sessionId);

  const [data, setData] = useState<SummaryData | null>(null);
  const [failed, setFailed] = useState(false);
  const [burst] = useState(() => Date.now());

  useEffect(() => {
    live
      .summary(id)
      .then(setData)
      .catch(() => setFailed(true));
  }, [id]);

  function download(format: "csv" | "json") {
    // A plain navigation carries the session cookie, so the server can
    // stream the file straight to disk with its own filename.
    window.location.href = `/api/sessions/${id}/export.${format}`;
  }

  if (failed) {
    return (
      <Centered>
        <div className="card">Could not load that session.</div>
      </Centered>
    );
  }
  if (!data) {
    return (
      <Centered>
        <Spinner size={28} />
      </Centered>
    );
  }

  const answered = data.slides.filter((s) => s.stats.total > 0).length;

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        padding: "48px 24px 60px",
        background:
          "radial-gradient(900px 500px at 80% -10%, rgba(232,184,74,0.14), transparent 60%)," +
          "radial-gradient(700px 480px at -10% 100%, rgba(95,168,189,0.14), transparent 60%)," +
          "var(--bg)",
      }}
    >
      <Confetti at={burst} />

      <div style={{ maxWidth: 1000, margin: "0 auto", position: "relative" }}>
        <header style={{ textAlign: "center", marginBottom: 40 }}>
          <p
            className="tiny"
            style={{ letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 600 }}
          >
            Session complete
          </p>
          <h1 style={{ fontSize: 38, margin: "10px 0 12px" }}>Thanks for the waves</h1>
          <p className="muted" style={{ fontSize: 16 }}>
            <b>{data.participants}</b> {data.participants === 1 ? "participant" : "participants"} ·{" "}
            <b>{data.total_answers}</b> {data.total_answers === 1 ? "answer" : "answers"} across{" "}
            <b>{data.slides.length}</b> questions
          </p>
        </header>

        <Podium leaders={data.leaderboard} />

        {data.leaderboard.length > 0 && (
          <div
            className="card"
            style={{ width: "min(560px, 100%)", margin: "0 auto 44px", padding: 24 }}
          >
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>Leaderboard</h2>
            {data.leaderboard.slice(0, 8).map((row, i) => (
              <div
                key={row.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "9px 0",
                  borderBottom:
                    i === Math.min(7, data.leaderboard.length - 1)
                      ? "none"
                      : "0.5px solid var(--outline-variant)",
                }}
              >
                <span className="tiny" style={{ width: 20 }}>
                  {i + 1}
                </span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{row.name}</span>
                <span style={{ fontSize: 16, fontWeight: 300 }}>{row.points} pts</span>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ fontSize: 20 }}>Results by question</h2>
          <span className="chip">
            {answered} of {data.slides.length} answered
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-outline" style={{ height: 40 }} onClick={() => download("csv")}>
            ↓ Export CSV
          </button>
          <button
            className="btn btn-outline"
            style={{ height: 40 }}
            onClick={() => download("json")}
          >
            ↓ Export JSON
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 14,
            marginBottom: 32,
          }}
        >
          {data.slides.map((slide, i) => {
            const accent = hueFor(i);
            const ink = hueInk(i);
            return (
              <a
                key={slide.id}
                href={`#q${slide.id}`}
                className="card"
                style={{
                  padding: 18,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 8,
                  textAlign: "left",
                  color: "var(--text-primary)",
                  textDecoration: "none",
                  animation: `cardIn 300ms var(--ease-default) ${i * 40}ms both`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SlideIcon type={slide.type} size={18} color={accent} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: ink }}>
                    {i + 1} · {LABELS[slide.type]}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
                  {slide.question || "Untitled"}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 500,
                    color: ink,
                    lineHeight: 1.2,
                    marginTop: 2,
                  }}
                >
                  {slide.headline.stat}
                </div>
                <div className="tiny">{slide.headline.sub}</div>
              </a>
            );
          })}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18, marginBottom: 36 }}>
          {data.slides.map((slide, index) => {
            const accent = hueFor(index);
            const ink = hueInk(index);
            return (
              <section
                key={slide.id}
                id={`q${slide.id}`}
                className="card"
                style={{
                  padding: 30,
                  scrollMarginTop: 20,
                  // Each block eases in as it comes into view, so the deck
                  // unfolds rather than landing all at once.
                  animation: `cardIn 420ms var(--ease-default) ${Math.min(index, 8) * 70}ms both`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <SlideIcon type={slide.type} size={20} color={accent} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: ink }}>
                    Question {index + 1} · {LABELS[slide.type]}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span className="chip">{slide.stats.total} answers</span>
                </div>

                <h3 style={{ fontSize: 24, margin: "12px 0 6px", lineHeight: 1.25 }}>
                  {slide.question || "Untitled"}
                </h3>

                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 24 }}>
                  <span style={{ fontSize: 30, fontWeight: 500, color: ink }}>
                    {slide.headline.stat}
                  </span>
                  <span className="tiny">{slide.headline.sub}</span>
                </div>

                {slide.stats.total === 0 ? (
                  <p className="muted">Nobody answered this one.</p>
                ) : (
                  <Results
                    type={slide.type}
                    config={slide.config}
                    stats={slide.stats}
                    accent={accent}
                    revealed
                    reveal={slide.config}
                  />
                )}
              </section>
            );
          })}
        </div>

        <footer style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
          <button className="btn btn-outline" onClick={() => navigate("/admin")}>
            Back to presentations
          </button>
          <button className="btn" onClick={() => navigate("/admin")}>
            Run it again
          </button>
        </footer>

        <p
          className="tiny"
          style={{
            textAlign: "center",
            marginTop: 34,
            display: "flex",
            gap: 8,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Logo size={16} /> Ripple · session {data.session.code}
          <button className="btn btn-text" style={{ fontSize: 12 }} onClick={() => void signOut()}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}

const LABELS: Record<SlideType, string> = {
  multiple: "Multiple choice",
  wordcloud: "Word cloud",
  scale: "Scales",
  ranking: "Ranking",
  guess: "Guess the number",
  grid: "2 × 2 grid",
  open: "Open ended",
  quiz: "Quiz · timed",
};

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>{children}</div>;
}
